import React, { createContext, useContext } from 'react';

/**
 * Lets any popup inside the profile sidebar (dropdown menus, popconfirms —
 * anything that portals to document.body so it can extend past the drawer's
 * width) report its open state to the sidebar. The sidebar dismisses itself
 * on any mousedown outside its panel, so while popups are open it must leave
 * dismissal to the popups themselves.
 *
 * Reports must come in balanced open/close pairs (close on unmount too) —
 * the sidebar tracks concurrently open popups with a counter. The
 * `useEffect(() => { if (!open) return; report(true); return () =>
 * report(false); }, [open])` shape guarantees this.
 *
 * Consumers outside a provider (e.g. tests) receive an inert no-op.
 */
type ReportPopupOpen = (open: boolean) => void;

const NOOP: ReportPopupOpen = () => undefined;

const ProfileSidebarPopupContext = createContext<ReportPopupOpen>(NOOP);

type ProfileSidebarPopupProviderProps = {
  onPopupOpenChange: ReportPopupOpen;
  children: React.ReactNode;
};

export const ProfileSidebarPopupProvider = ({
  onPopupOpenChange,
  children,
}: ProfileSidebarPopupProviderProps): JSX.Element => (
  <ProfileSidebarPopupContext.Provider value={onPopupOpenChange}>
    {children}
  </ProfileSidebarPopupContext.Provider>
);

export const useReportProfileSidebarPopup = (): ReportPopupOpen =>
  useContext(ProfileSidebarPopupContext);
